import { FC } from 'react';
import dayjs from 'dayjs';
// Models
import { RootState } from 'app/store';
import IBillingInvoice from 'app/models/BillingInvoice';
// Redux
import { useAppSelector } from 'app/hooks/useStore';
// Selectors
import { selectCompanyNameByAccountId } from 'app/store/Clients/Clients.selectors';
// Mui
import { Box, Tooltip, Typography, Paper, Theme } from '@mui/material';
// Icons
import { Receipt as ReceiptIcon } from '@mui/icons-material';
// Components
import Icon from 'app/components/Icon';
import { InfoBlock } from 'app/components/Utilities';
// Hooks
import useFormattedDate from 'app/hooks/useFormattedDate';
// 
import InvoicesListItemLines from './InvoicesListItemLines';
import InvoicesListItemTransactions from './InvoicesListItemTransactions';
// i18next
import { useTranslation } from 'react-i18next';
// Utilities
import { statusThemeColor } from 'app/utilities/Utilities';

type Props = {
  invoice: IBillingInvoice;
};

const InvoiceCard:FC<Props> = ({
  // Props
  invoice
}) => {
  const { t } = useTranslation();
  // State
  const companyName = useAppSelector((state:RootState) => selectCompanyNameByAccountId(state, {
    accountId: invoice.insuranceCase?.clientAccountId
  }));

  const dueDate = useFormattedDate(invoice.dueDate);
  const periodStart = useFormattedDate(invoice.periodStart);
  const periodEnd = useFormattedDate(dayjs(invoice.periodEnd).subtract(1, 'day').toISOString());

  const dueDateIsInPast = invoice.status === 'sent' && invoice.dueDate
    ? dayjs(invoice.dueDate).isBefore(dayjs(), 'day')
    : false
  ;

  const statusLabels:Record<string, string> = {
    sent: t('labels.outstanding'),
    paid: t('labels.paid')
  }

  return (
    <Paper sx={{ p: 4, '& + &': { mt: 4 } }}>
      <Box display="flex" justifyContent="center" alignItems="center">
        {invoice.description ? (
          <Typography variant="subtitle1">{invoice.description}</Typography>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          variant="body2"
          sx={{
            textTransform: 'capitalize',
            color: (theme:Theme) => statusThemeColor(invoice.status, theme)
          }}
        >
          {statusLabels[invoice.status]}
          {dueDateIsInPast ? (
            <Box component="span" color="red">{` (${t('labels.overdue')})`}</Box>
          ) : null}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {periodStart && periodEnd ? (
          <InfoBlock
            label={t('labels.billingPeriod')}
            value={`${periodStart} - ${periodEnd}`}
            direction="row"
          />
        ) : null}
        {dueDate ? (
          <InfoBlock label={t('labels.dueDate')} value={dueDate} direction="row" />
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 4 }}>
        {companyName ? (
          <Tooltip title={t('labels.clientName')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Icon icon="team" />
              <Typography variant="subtitle2">{companyName}</Typography>
            </Box>
          </Tooltip>
        ) : null}
        {invoice.insuranceCase && invoice.insuranceCase.claimNumber  ? (
          <Tooltip title={t('labels.claimNumber')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Icon icon="case_number" />
              <Typography variant="subtitle2">{invoice.insuranceCase.claimNumber}</Typography>
            </Box>
          </Tooltip>
        ) : null}
        {invoice.invoiceNumber  ? (
          <Tooltip title={t('labels.invoiceNumber')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ReceiptIcon />
              <Typography variant="body2">{invoice.invoiceNumber}</Typography>
            </Box>
          </Tooltip>
        ) : null}
      </Box>

      <InvoicesListItemTransactions
        invoiceId={invoice.id}
        transactions={invoice.transactions}
      />
      <InvoicesListItemLines 
        invoiceId={invoice.id}
        invoiceDiscountAmount={invoice.discountAmount}
        lines={invoice.lines}
      />
    </Paper>
  )
}

export default InvoiceCard;

